import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DialogContent, Table, TableBody, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import {
  StyledDialog,
  StyledDialogTitle,
  CloseButton,
  SubTitle,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  DateCell,
  StatusCell,
  AvailableStatus,
  UnavailableStatus
} from './styles';
import { fetchWorkerPreferredTimings } from '../../../../../modules/worker/actions';
import { selectWorkerPreferredTimingsData, selectWorkerPreferredTimingsLoading } from '../../../../../modules/worker/selectors';
import { AppDispatch } from '../../../../../store';
import { IWorkerPreferredTiming } from '../../../../../modules/worker/types';

interface PreferredTimesProps {
  open: boolean;
  onClose: () => void;
  workerId: number;
}

const PreferredTimes: React.FC<PreferredTimesProps> = ({ open, onClose, workerId }) => {
  const dispatch: AppDispatch = useDispatch();
  const timingsData = useSelector(selectWorkerPreferredTimingsData);
  const loading = useSelector(selectWorkerPreferredTimingsLoading);

  const [list, setList] = useState<IWorkerPreferredTiming[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setList([]);
      setPage(1);
      setHasMore(true);
      if (workerId) {
        dispatch(fetchWorkerPreferredTimings({ workerId, page: 1, pageSize: 10 }));
      }
    }
  }, [open, workerId, dispatch]);

  // Append data when new results arrive
  useEffect(() => {
    if (timingsData?.data?.results) {
      const newItems = timingsData.data.results;

      setList(prev => {
        // If it's the first page (or we reset), and pre-existing list is empty, just set it.
        // But to be safe against race conditions (e.g. page 1 re-fetched), filter duplicates.
        const existingIds = new Set(prev.map(i => i.worker_preferred_timings_id));
        const uniqueNewItems = newItems.filter(i => !existingIds.has(i.worker_preferred_timings_id));
        return [...prev, ...uniqueNewItems];
      });

      if (!timingsData.data.next) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    }
  }, [timingsData]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) { // Threshold
      if (hasMore && !loading) {
        const nextPage = page + 1;
        setPage(nextPage);
        dispatch(fetchWorkerPreferredTimings({ workerId, page: nextPage, pageSize: 10 }));
      }
    }
  };

  // Group data by date using local list
  const groupedData: { [key: string]: string[] } = {};

  if (list) {
    list.forEach((item) => {
      if (!groupedData[item.preferred_date]) {
        groupedData[item.preferred_date] = [];
      }
      groupedData[item.preferred_date].push(item.preferred_timings.toLowerCase());
    });
  }

  // Sort dates
  const sortedDates = Object.keys(groupedData).sort((a, b) => dayjs(a).diff(dayjs(b)));

  const isAvailable = (timings: string[], type: string) => {
    // Mapping backend exact values to columns
    // 'both' typically implies dual availability, but based on screenshot if there is an "All Shift" column, 
    // and sample data has 'both', and screenshot shows 'All Shift' ticked, we map 'both' -> All Shift?
    // OR 'both' means AM and PM?
    // Let's assume 'both' maps to All Shift column for now as per user request context often matching UI to data.

    // Strict check
    if (type === 'all' && timings.includes('both')) return true; // Mapping 'both' to All Shift col
    if (timings.includes(type)) return true;

    return false;
  };

  const renderStatus = (available: boolean) => {
    return available ? (
      <AvailableStatus>
        <CheckCircleIcon /> Available
      </AvailableStatus>
    ) : (
      <UnavailableStatus>-</UnavailableStatus>
    );
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md">
      <StyledDialogTitle>
        Worker Availability
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </StyledDialogTitle>
      <SubTitle>Preferences set by worker</SubTitle>

      <DialogContent sx={{ padding: 0 }}>
        <StyledTableContainer onScroll={handleScroll}>
          <Table stickyHeader>
            <StyledTableHead>
              <StyledTableRow>
                <StatusCell width="20%">Day/Date</StatusCell>
                <StatusCell width="20%">AM<br />(07.00 - 15.00)</StatusCell>
                <StatusCell width="20%">PM<br />(15.00 - 22.00)</StatusCell>
                <StatusCell width="20%">Night<br />(22.00 - 07.00)</StatusCell>
                <StatusCell width="20%">All Shift</StatusCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {sortedDates.length > 0 ? (
                sortedDates.map((date) => {
                  const timings = groupedData[date];
                  const dayObj = dayjs(date);
                  return (
                    <StyledTableRow key={date}>
                      <DateCell>
                        {dayObj.format('DD/MM/YYYY')}
                        <span>{dayObj.format('ddd')}</span>
                      </DateCell>
                      <StatusCell>{renderStatus(isAvailable(timings, 'am'))}</StatusCell>
                      <StatusCell>{renderStatus(isAvailable(timings, 'pm'))}</StatusCell>
                      <StatusCell>{renderStatus(isAvailable(timings, 'night'))}</StatusCell>
                      <StatusCell>{renderStatus(isAvailable(timings, 'all'))}</StatusCell> {/* Checking against 'all' which checks for 'both' inside helper */}
                    </StyledTableRow>
                  );
                })
              ) : (
                !loading && (
                  <StyledTableRow>
                    <StatusCell colSpan={5} align="center">
                      <Typography color="textSecondary" sx={{ py: 2 }}>
                        No preferred timings data available.
                      </Typography>
                    </StatusCell>
                  </StyledTableRow>
                )
              )}
              {loading && (
                <StyledTableRow>
                  <StatusCell colSpan={5} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </StatusCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default PreferredTimes;
